import React, { useState, useEffect } from "react";
import {
  CircleChart,
  LineDashedChart,
  BasicColumnChart,
  BasicColumnChartForJob,
  CircleChartManager,
  LineDashedChartManager,
  BasicColumnChartForJobAiAnalysisManager,
} from "../components/ContainerChart";

import ChartMiniGreen from "../assets/ChartMiniGreen.svg";
import ChartMiniRed from "../assets/ChartMiniRed.png";
import ChartMiniOrange from "../assets/ChartMiniOrange.png";
import { jobsEndpoint } from "../constants";

const AiAnalysis = () => {
  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    activeJobs: 0,
    totalApplicants: 0,
    avgTimeToHire: "0 Weeks",
    jobGrowth: 0,
    applicantGrowth: 0,
    hireTimeGrowth: 0,
    loading: true,
    error: null
  });

  const [pipelineData, setPipelineData] = useState([
    { stage: "Applied", count: 0, percentage: 85 },
    { stage: "Shortlisted", count: 0, percentage: 65 },
    { stage: "Interviews", count: 0, percentage: 45 },
    { stage: "Offer Sent", count: 0, percentage: 25 },
    { stage: "Hired", count: 0, percentage: 15 },
  ]);

  // API base URL - adjust this to your Django backend URL
  const API_BASE_URL = jobsEndpoint;

  // Helper function to make API calls with authentication
  const apiCall = async (endpoint) => {
    try {
      const token = localStorage.getItem('token'); // Adjust based on your auth implementation
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          // Add authorization header since endpoints require authentication
          'Authorization': `Token ${token}`,
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required. Please log in.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      throw error;
    }
  };

  // Fetch data from Django backend using the new consolidated endpoint
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setDashboardData(prev => ({ ...prev, loading: true, error: null }));

        // Use the new consolidated dashboard analytics endpoint
        const dashboardResponse = await apiCall('dashboard_analytics');

        // Set dashboard data directly from the API response
        setDashboardData({
          activeJobs: dashboardResponse.activeJobs,
          totalApplicants: dashboardResponse.totalApplicants,
          avgTimeToHire: dashboardResponse.avgTimeToHire,
          jobGrowth: dashboardResponse.jobGrowth,
          applicantGrowth: dashboardResponse.applicantGrowth,
          hireTimeGrowth: dashboardResponse.hireTimeGrowth,
          loading: false,
          error: null
        });

        // Set pipeline data from the API response
        setPipelineData(dashboardResponse.pipelineData);

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setDashboardData(prev => ({
          ...prev,
          loading: false,
          error: error.message || 'Failed to load dashboard data. Please try again.'
        }));
      }
    };

    fetchDashboardData();
  }, []);

  // Optional: Function to refresh data manually
  const refreshDashboard = async () => {
    try {
      setDashboardData(prev => ({ ...prev, loading: true }));
      
      const dashboardResponse = await apiCall('dashboard-analytics');
      
      setDashboardData({
        activeJobs: dashboardResponse.activeJobs,
        totalApplicants: dashboardResponse.totalApplicants,
        avgTimeToHire: dashboardResponse.avgTimeToHire,
        jobGrowth: dashboardResponse.jobGrowth,
        applicantGrowth: dashboardResponse.applicantGrowth,
        hireTimeGrowth: dashboardResponse.hireTimeGrowth,
        loading: false,
        error: null
      });

      setPipelineData(dashboardResponse.pipelineData);
      
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
      setDashboardData(prev => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to refresh dashboard data.'
      }));
    }
  };

  // Optional: Function to fetch individual data if needed
  const fetchIndividualData = async () => {
    try {
      setDashboardData(prev => ({ ...prev, loading: true, error: null }));

      // If you need to use the individual endpoints for any reason
      const [jobsResponse, applicantsResponse, applicationsResponse] = await Promise.all([
        apiCall('list'),
        apiCall('applicants_list/'),
        apiCall('applications_list/')
      ]);

      // Process the individual responses if needed
      const activeJobs = jobsResponse.results?.length || 0;
      const totalApplicants = applicantsResponse.results?.length || 0;
      const applications = applicationsResponse.results || [];

      console.log('Individual data fetched:', {
        jobs: jobsResponse,
        applicants: applicantsResponse,
        applications: applicationsResponse
      });

    } catch (error) {
      console.error('Error fetching individual data:', error);
    }
  };


  const [pipelineMockData, setPipelineMockData] = useState([
    { stage: "Applied", count: 1286, percentage: 85 },
    { stage: "Shortlisted", count: 864, percentage: 65 },
    { stage: "Interviews", count: 624, percentage: 45 },
    { stage: "Offer Sent", count: 246, percentage: 25 },
    { stage: "Hired", count: 124, percentage: 15 },
  ]);
// Loading state
  if (dashboardData.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (dashboardData.error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-6 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 mb-4">{dashboardData.error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex w-full justify-start items-start flex-row ">
        <div className=" flex-1 flex-col gap-6 pt-2 w-full">
          <div className="flex self-stretch justify-start items-start flex-col gap-8 pt-[32px] pb-[48px] ">
            <div className="flex self-stretch justify-start items-start flex-col gap-6">
              <div className="flex self-stretch justify-start items-start flex-col gap-5 px-[32px]">
                <div className="flex self-stretch justify-start items-start flex-col gap-5">
                  <div className="flex flex-wrap self-stretch justify-start items-start flex-row gap-y-5 gap-x-4">
                    <div className="min-w-[320px] flex flex-1 justify-start items-start flex-col gap-1">
                      <p className="self-stretch text-[#181D27] text-2xl  font-semibold">
                        AI Analysis
                      </p>
                      <p className="self-stretch text-[#535862] ">
                        Here is a complete AI overview of your system.
                      </p>
                    </div>
                    <div className="flex justify-start items-center flex-row gap-3">
                    
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex self-stretch justify-start items-start flex-col gap-6">
               <div className="flex self-stretch justify-start items-start flex-col gap-6">
                          <div className="flex self-stretch justify-start items-start flex-col gap-6 px-[32px]">
                            <div className="flex flex-wrap self-stretch justify-start items-start flex-row gap-6">
                              <div className="min-w-[320px] flex flex-1 justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                                <div className="flex self-stretch justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-center flex-row gap-4 pt-3 pb-2 px-[20px]">
                                    <p className="flex-1 text-[#181D27] text-sm  font-semibold">
                                      Active Job Openings
                                    </p>
                                  </div>
                                  <div className="flex self-stretch justify-start items-start flex-col gap-5 p-[20px] bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)] relative">
                                    <div className="flex flex-wrap self-stretch justify-start items-center flex-row gap-3">
                                      <span className="text-[#181D27] text-3xl  font-semibold">
                                        2,420
                                      </span>
                                      <div className="flex justify-start items-center flex-row gap-2">
                                        <div className="flex justify-center items-center flex-row gap-1">
                                          <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M14.6667 4.66602L9.42092 9.91177C9.15691 10.1758 9.02491 10.3078 8.87269 10.3572C8.73879 10.4008 8.59456 10.4008 8.46067 10.3572C8.30845 10.3078 8.17644 10.1758 7.91243 9.91177L6.08759 8.08693C5.82358 7.82292 5.69157 7.69091 5.53936 7.64145C5.40546 7.59795 5.26123 7.59795 5.12733 7.64145C4.97511 7.69091 4.84311 7.82292 4.5791 8.08693L1.33334 11.3327M14.6667 4.66602H10M14.6667 4.66602V9.33268"
                                              stroke="#17B26A"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                          <span className="text-[#079455] text-sm  text-center font-medium">
                                            40%
                                          </span>
                                        </div>
                                        <span className="text-[#535862] text-sm  font-medium">
                                          vs last month
                                        </span>
                                      </div>
                                    </div>
                        
                                    <div>
                                      <img src={ChartMiniGreen} alt="" />
                                    </div>
                                    <div
                                      className="flex justify-start items-start flex-col absolute"
                                      style={{
                                        right: "20px",
                                        width: "20px",
                                        top: "20px",
                                        height: "20px",
                                      }}
                                    >
                                      <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M10 10.834C10.4603 10.834 10.8334 10.4609 10.8334 10.0007C10.8334 9.54041 10.4603 9.16732 10 9.16732C9.53978 9.16732 9.16669 9.54041 9.16669 10.0007C9.16669 10.4609 9.53978 10.834 10 10.834Z"
                                          stroke="#A4A7AE"
                                          strokeWidth="1.66667"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M10 5.00065C10.4603 5.00065 10.8334 4.62755 10.8334 4.16732C10.8334 3.70708 10.4603 3.33398 10 3.33398C9.53978 3.33398 9.16669 3.70708 9.16669 4.16732C9.16669 4.62755 9.53978 5.00065 10 5.00065Z"
                                          stroke="#A4A7AE"
                                          strokeWidth="1.66667"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M10 16.6673C10.4603 16.6673 10.8334 16.2942 10.8334 15.834C10.8334 15.3737 10.4603 15.0007 10 15.0007C9.53978 15.0007 9.16669 15.3737 9.16669 15.834C9.16669 16.2942 9.53978 16.6673 10 16.6673Z"
                                          stroke="#A4A7AE"
                                          strokeWidth="1.66667"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="min-w-[320px] flex flex-1 justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                                <div className="flex self-stretch justify-start items-start flex-col gap-0.5">
                                  <div className="flex self-stretch justify-start items-center flex-row gap-4 pt-3 pb-2 px-[20px]">
                                    <p className="flex-1 text-[#181D27] text-sm  font-semibold">
                                      Total Applicants
                                    </p>
                                  </div>
                                  <div className="flex self-stretch justify-start items-start flex-col gap-5 p-[20px] bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)] relative">
                                    <div className="flex flex-wrap self-stretch justify-start items-center flex-row gap-3">
                                      <span className="text-[#181D27] text-3xl  font-semibold">
                                        1,210
                                      </span>
                                      <div className="flex justify-start items-center flex-row gap-2">
                                        <div className="flex justify-center items-center flex-row gap-1">
                                          <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M14.6666 11.3327L9.42089 6.08693C9.15688 5.82292 9.02488 5.69091 8.87266 5.64145C8.73876 5.59795 8.59453 5.59795 8.46064 5.64145C8.30842 5.69091 8.17641 5.82292 7.9124 6.08693L6.08756 7.91177C5.82355 8.17578 5.69154 8.30779 5.53932 8.35724C5.40543 8.40075 5.2612 8.40075 5.1273 8.35724C4.97508 8.30779 4.84308 8.17578 4.57907 7.91177L1.33331 4.66602M14.6666 11.3327H9.99998M14.6666 11.3327V6.66602"
                                              stroke="#F04438"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                          <span className="text-[#D92D20] text-sm  text-center font-medium">
                                            10%
                                          </span>
                                        </div>
                                        <span className="text-[#535862] text-sm  font-medium">
                                          vs last month
                                        </span>
                                      </div>
                                    </div>
                              
                                    <img src={ChartMiniRed} alt="" />
                                    <div
                                      className="flex justify-start items-start flex-col absolute"
                                      style={{
                                        right: "20px",
                                        width: "20px",
                                        top: "20px",
                                        height: "20px",
                                      }}
                                    >
                                      <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M10 10.834C10.4603 10.834 10.8334 10.4609 10.8334 10.0007C10.8334 9.54041 10.4603 9.16732 10 9.16732C9.53978 9.16732 9.16669 9.54041 9.16669 10.0007C9.16669 10.4609 9.53978 10.834 10 10.834Z"
                                          stroke="#A4A7AE"
                                          strokeWidth="1.66667"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M10 5.00065C10.4603 5.00065 10.8334 4.62755 10.8334 4.16732C10.8334 3.70708 10.4603 3.33398 10 3.33398C9.53978 3.33398 9.16669 3.70708 9.16669 4.16732C9.16669 4.62755 9.53978 5.00065 10 5.00065Z"
                                          stroke="#A4A7AE"
                                          strokeWidth="1.66667"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M10 16.6673C10.4603 16.6673 10.8334 16.2942 10.8334 15.834C10.8334 15.3737 10.4603 15.0007 10 15.0007C9.53978 15.0007 9.16669 15.3737 9.16669 15.834C9.16669 16.2942 9.53978 16.6673 10 16.6673Z"
                                          stroke="#A4A7AE"
                                          strokeWidth="1.66667"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </div>
                                  </div>
                                </div>
                              </div>
                                         <div className="min-w-[320px] flex flex-1 justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                    <div className="flex self-stretch justify-start items-start flex-col gap-0.5">
                      <div className="flex self-stretch justify-start items-center flex-row gap-4 pt-3 pb-2 px-[20px]">
                        <p className="flex-1 text-[#181D27] text-sm  font-semibold">
                          Avg. time to hire
                        </p>
                      </div>
                      <div className="flex self-stretch justify-start items-start flex-col gap-5 p-[20px] bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)] relative">
                        <div className="flex flex-wrap self-stretch justify-start items-center flex-row gap-3">
                          <span className="text-[#181D27] text-3xl  font-semibold">
                            2 Weeks
                          </span>
                          <div className="flex justify-start items-center flex-row gap-2">
                            <div className="flex justify-center items-center flex-row gap-1">
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M14.6667 4.66602L9.42095 9.91177C9.15694 10.1758 9.02494 10.3078 8.87272 10.3572C8.73882 10.4008 8.59459 10.4008 8.4607 10.3572C8.30848 10.3078 8.17647 10.1758 7.91246 9.91177L6.08762 8.08693C5.82361 7.82292 5.6916 7.69091 5.53939 7.64145C5.40549 7.59795 5.26126 7.59795 5.12736 7.64145C4.97514 7.69091 4.84314 7.82292 4.57913 8.08693L1.33337 11.3327M14.6667 4.66602H10M14.6667 4.66602V9.33268"
                                  stroke="#17B26A"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span className="text-[#079455] text-sm  text-center font-medium">
                                20%
                              </span>
                            </div>
                            <span className="text-[#535862] text-sm  font-medium">
                              vs last month
                            </span>
                          </div>
                        </div>
                       
                        <div>
                          <img src={ChartMiniOrange} alt="" />
                        </div>
                        <div
                          className="flex justify-start items-start flex-col absolute"
                          style={{
                            right: "20px",
                            width: "20px",
                            top: "20px",
                            height: "20px",
                          }}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9.99996 10.834C10.4602 10.834 10.8333 10.4609 10.8333 10.0007C10.8333 9.54041 10.4602 9.16732 9.99996 9.16732C9.53972 9.16732 9.16663 9.54041 9.16663 10.0007C9.16663 10.4609 9.53972 10.834 9.99996 10.834Z"
                              stroke="#A4A7AE"
                              strokeWidth="1.66667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M9.99996 5.00065C10.4602 5.00065 10.8333 4.62755 10.8333 4.16732C10.8333 3.70708 10.4602 3.33398 9.99996 3.33398C9.53972 3.33398 9.16663 3.70708 9.16663 4.16732C9.16663 4.62755 9.53972 5.00065 9.99996 5.00065Z"
                              stroke="#A4A7AE"
                              strokeWidth="1.66667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M9.99996 16.6673C10.4602 16.6673 10.8333 16.2942 10.8333 15.834C10.8333 15.3737 10.4602 15.0007 9.99996 15.0007C9.53972 15.0007 9.16663 15.3737 9.16663 15.834C9.16663 16.2942 9.53972 16.6673 9.99996 16.6673Z"
                              stroke="#A4A7AE"
                              strokeWidth="1.66667"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                            </div>
                          </div>
                        </div>

              {/* Recruitment Pipeline */}
              <div className="flex self-stretch justify-start items-start flex-col gap-6 px-[32px]">
                <div className="flex self-stretch justify-start items-start flex-col gap-6 p-6 bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                  <div className="flex self-stretch justify-start items-start flex-col gap-1">
                    <p className="self-stretch text-[#181D27] text-lg font-semibold">
                      Recruitment Pipeline
                    </p>
                    <p className="self-stretch text-[#535862] text-sm">
                      Track candidates through each stage of your hiring process
                    </p>
                  </div>
                  
                  <div className="flex self-stretch justify-start items-start flex-col gap-4">
                    {pipelineData.map((stage, index) => (
                      <div key={index} className="flex self-stretch justify-between items-center flex-row gap-4 p-4 bg-[#F8F9FA] border-solid border-[#E9EAEB] border rounded-lg">
                        <div className="flex justify-start items-center flex-row gap-3">
                          <div className={`w-3 h-3 rounded-full ${
                            stage.stage === 'Applied' ? 'bg-[#0066CC]' :
                            stage.stage === 'Shortlisted' ? 'bg-[#17B26A]' :
                            stage.stage === 'Interviews' ? 'bg-[#F79009]' :
                            stage.stage === 'Offer Sent' ? 'bg-[#7C3AED]' :
                            'bg-[#06AED4]'
                          }`}></div>
                          <span className="text-[#181D27] text-sm font-medium">
                            {stage.stage}
                          </span>
                        </div>
                        <div className="flex justify-end items-center flex-row gap-4">
                          <span className="text-[#181D27] text-sm font-semibold">
                            {stage.count || 0}
                          </span>
                          <div className="w-20 bg-[#E9EAEB] rounded-full h-2">
                            <div 
                              className="bg-[#0066CC] h-2 rounded-full transition-all duration-300"
                              style={{ width: `${stage.percentage || 0}%` }}
                            ></div>
                          </div>
                          <span className="text-[#535862] text-sm w-12 text-right">
                            {stage.percentage || 0}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
     <div className="px-[32px] w-full">
              <div className="grid xl:grid-cols-2 gap-6">
                <div
                  className="flex w-full justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)] "
                  // style={{ width: "451px" }}
                >
                  <div className="flex self-stretch justify-start items-start flex-col gap-0.5">
                    <div className="flex self-stretch justify-start items-center flex-row gap-4 pt-3 pb-2 px-[20px]">
                      <p className="flex-1 text-[#181D27] text-sm  font-semibold">
                        Active now
                      </p>
                    </div>
                    <div className=" h-full w-full justify-start items-start flex-col bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                      <CircleChart />
                    </div>
                  </div>
                </div>
                <div className="bg-white  rounded-xl border border-gray-200">
                  <div className="bg-gray-50 py-3 px-5">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Hiring Pipeline Breakdown
                    </h3>
                  </div>

                                   <div className=" h-96 grid grid-cols-5 gap-3 py-3 px-5 border-t rounded-t-md border-gray-200">
                    {pipelineMockData.map((stage, index) => (
                      <div
                        key={stage.stage}
                        className="border flex flex-col gap-4 justify-between rounded-md border-gray-200 p-2"
                      >
                        <div className=" flex flex-col gap-2 text-sm mb-1">
                          <p className="text-gray-600 text-md font-semibold">
                            Step {index + 1}
                          </p>
                          <h3 className="text-gray-900 xl:text-sm text-xs font-semibold">
                            {stage.stage}
                          </h3>
                          {/* <span className="font-medium text-gray-900">
                    {stage.count.toLocaleString()}
                  </span> */}
                        </div>
                        <div className=" relative h-40 rotate-180  bg-transparent overflow-hidden">
                          <div
                            className="h-full bg-[#19BFB7] rounded-lg"
                            style={{ height: `${stage.percentage}%` }}
                          />
                          <span className="font-medium absolute top-1 -translate-x-1/2 left-1/2 rotate-180 text-white">
                            {stage.count.toLocaleString()}
                          </span>
                        </div>

                        <div className="text-xs flex gap-3 flex-col text-gray-500 mt-1">
                          <h4>Dropout ratio</h4>
                          <div className="flex items-center gap-1 text-green-600 font-semibold">
                            <span className="text-gray-900">0%</span>
                            <span>↑ 8.6%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                
                </div>
              </div>
            </div>
            <div className="flex self-stretch justify-start items-start flex-row gap-6">
              <div className="flex flex-1 justify-start items-start flex-row gap-6 px-[32px]">
                <div className="flex flex-1 justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                  <div className="flex self-stretch justify-start items-start flex-col gap-0.5">
                    <div className="flex self-stretch justify-start items-center flex-row gap-4 pt-3 pb-2 px-[20px]">
                      <p className="flex-1 text-[#181D27] text-sm  font-semibold">
                        Avg. Time to Hire
                      </p>
                    </div>
                    <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                      <div className="flex self-stretch justify-start items-start flex-col gap-5 p-[20px] rounded-xl">
                        <div className="flex self-stretch justify-start items-start flex-row gap-4">
                          <div className="flex flex-1 justify-start items-start flex-col gap-2">
                            <div className="flex self-stretch justify-start items-center flex-row gap-3">
                              <span className="text-[#181D27] text-3xl  font-semibold">
                                2 Weeks
                              </span>
                              <div className="flex justify-start items-center flex-row gap-1 py-0.5 pr-2 pl-1.5 bg-[#FFFFFF] border-solid border-[#D5D7DA] border rounded-md shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                                <svg
                                  width="12"
                                  height="12"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M3.5 8.5L8.5 3.5M8.5 3.5H3.5M8.5 3.5V8.5"
                                    stroke="#17B26A"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                <span className="text-[#414651] text-sm  text-center font-medium">
                                  2.4%
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-start items-start flex-row gap-3">
                            <div className="flex justify-start items-center flex-row gap-0.5 bg-[#FAFAFA] border-solid border-[#E9EAEB] border rounded-lg">
                              <div className="flex justify-center items-center flex-row gap-2 py-2 px-3 bg-[#FFFFFF] border-solid border-[#D5D7DA] border rounded-lg shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)] h-[36px]">
                                <span className="text-[#414651] text-sm  font-semibold">
                                  12 months
                                </span>
                              </div>
                              <div className="flex justify-center items-center flex-row gap-2 py-2 px-3 rounded-lg h-[36px]">
                                <span className="text-[#717680] text-sm  font-semibold">
                                  30 days
                                </span>
                              </div>
                              <div className="flex justify-center items-center flex-row gap-2 py-2 px-3 rounded-lg h-[36px]">
                                <span className="text-[#717680] text-sm  font-semibold">
                                  7 days
                                </span>
                              </div>
                              <div className="flex justify-center items-center flex-row gap-2 py-2 px-3 rounded-lg h-[36px]">
                                <span className="text-[#717680] text-sm  font-semibold">
                                  24 hours
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
          <div className="w-full px-5">
                        <LineDashedChartManager />
                      </div>
            
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full gap-6">
              <div className="grid w-full lg:grid-cols-2 gap-6 px-[32px]">
                <div className=" w-full bg-[#FDFDFD] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                  <div className=" justify-start items-start flex-col gap-0.5">
                    <div className="flex self-stretch justify-start items-center flex-row gap-4 pt-3 pb-2 px-[20px]">
                      <p className="flex-1 text-[#181D27] text-sm  font-semibold">
                        Candidate Quality
                      </p>
                    </div>
                    <div className=" bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                      <div className="w-full px-7">
                        <BasicColumnChart />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-1 justify-start items-start flex-col bg-[#FDFDFD] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                  <div className="flex self-stretch justify-start items-start flex-col gap-0.5">
                    <div className="flex self-stretch justify-start items-center flex-row gap-4 pt-3 pb-2 px-[20px]">
                      <p className="flex-1 text-[#181D27] text-sm  font-semibold">
                        Job Listing Performance
                      </p>
                    </div>
                    <div className="flex self-stretch justify-start items-start flex-col bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                      <div className="w-full px-7">
                        <BasicColumnChartForJob />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default AiAnalysis;